import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Ban, Undo2 } from "lucide-react";
import { adminApi, apiError, MODALITY_LABEL } from "@/components/admin/api";
import StatusBadge from "@/components/admin/StatusBadge";

const WEEKDAY_SHORT = ["seg", "ter", "qua", "qui", "sex", "sáb", "dom"];
const MONTH_NAMES = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

const toISO = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const mondayOf = (d) => {
  const x = new Date(d);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
};

const AgendaView = () => {
  const [mode, setMode] = useState("dia");
  const [refDate, setRefDate] = useState(() => new Date());
  const [agenda, setAgenda] = useState(null);
  const [acting, setActing] = useState(false);

  const range = useMemo(() => {
    if (mode === "dia") return { start: toISO(refDate), end: toISO(refDate) };
    if (mode === "semana") {
      const mon = mondayOf(refDate);
      const sun = new Date(mon);
      sun.setDate(sun.getDate() + 6);
      return { start: toISO(mon), end: toISO(sun) };
    }
    const first = new Date(refDate.getFullYear(), refDate.getMonth(), 1);
    const last = new Date(refDate.getFullYear(), refDate.getMonth() + 1, 0);
    return { start: toISO(first), end: toISO(last) };
  }, [mode, refDate]);

  const load = useCallback(() => {
    setAgenda(null);
    adminApi
      .get("/admin/journey/agenda", { params: range })
      .then((res) => setAgenda(res.data))
      .catch((e) => toast.error(apiError(e, "Não foi possível carregar a agenda.")));
  }, [range]);

  useEffect(load, [load]);

  const shift = (dir) => {
    setRefDate((d) => {
      const x = new Date(d);
      if (mode === "dia") x.setDate(x.getDate() + dir);
      else if (mode === "semana") x.setDate(x.getDate() + 7 * dir);
      else x.setMonth(x.getMonth() + dir);
      return x;
    });
  };

  const toggleBlock = async (slot, date) => {
    setActing(true);
    try {
      if (slot.status === "bloqueado") {
        await adminApi.delete(`/admin/journey/blocks/${date}/${slot.time}`);
        toast.success("Horário desbloqueado.");
      } else {
        await adminApi.post("/admin/journey/blocks", { date, time: slot.time });
        toast.success("Horário bloqueado — pacientes não poderão escolhê-lo.");
      }
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setActing(false);
    }
  };

  const bookingAction = async (protocol, action) => {
    if (action === "cancelar" && !window.confirm("Cancelar este agendamento?")) return;
    setActing(true);
    try {
      await adminApi.patch(`/admin/journey/bookings/${protocol}`, { action });
      toast.success("Agendamento atualizado.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setActing(false);
    }
  };

  const canConfirmSlot = (s) => ["aguardando_pagamento", "agendada"].includes(s.booking?.status);
  const canRealizeSlot = (s) => ["agendada", "confirmada", "remarcada"].includes(s.booking?.status);
  const canCancelSlot = (s) => ["aguardando_pagamento", "agendada", "confirmada", "remarcada"].includes(s.booking?.status);

  const periodLabel = useMemo(() => {
    if (mode === "dia")
      return refDate.toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    if (mode === "semana") {
      const mon = mondayOf(refDate);
      const sun = new Date(mon);
      sun.setDate(sun.getDate() + 6);
      return `${mon.getDate()} ${MONTH_NAMES[mon.getMonth()]} — ${sun.getDate()} ${MONTH_NAMES[sun.getMonth()]} ${sun.getFullYear()}`;
    }
    return `${MONTH_NAMES[refDate.getMonth()]} de ${refDate.getFullYear()}`;
  }, [mode, refDate]);

  const dayMap = useMemo(() => {
    const m = {};
    (agenda?.days || []).forEach((d) => (m[d.date] = d));
    return m;
  }, [agenda]);

  const openDay = (isoDate) => {
    const [y, m, d] = isoDate.split("-").map(Number);
    setRefDate(new Date(y, m - 1, d));
    setMode("dia");
  };

  return (
    <div data-testid="agenda-view">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <button type="button" data-testid="agenda-prev" aria-label="Anterior" onClick={() => shift(-1)} className="flex h-10 w-10 items-center justify-center rounded-full border border-raiz-800/20 text-raiz-800 hover:border-raiz-800">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button type="button" data-testid="agenda-hoje" onClick={() => setRefDate(new Date())} className="rounded-full border border-raiz-800/20 px-4 py-2 text-xs font-medium text-raiz-800 hover:border-raiz-800">
            Hoje
          </button>
          <button type="button" data-testid="agenda-next" aria-label="Próximo" onClick={() => shift(1)} className="flex h-10 w-10 items-center justify-center rounded-full border border-raiz-800/20 text-raiz-800 hover:border-raiz-800">
            <ChevronRight className="h-4 w-4" />
          </button>
          <p className="ml-2 font-serif text-xl text-ink first-letter:uppercase" data-testid="agenda-periodo">{periodLabel}</p>
        </div>
        <div className="flex rounded-full border border-raiz-900/15 p-1" data-testid="agenda-modos">
          {["dia", "semana", "mes"].map((m) => (
            <button
              key={m}
              type="button"
              data-testid={`agenda-modo-${m}`}
              onClick={() => setMode(m)}
              className={`rounded-full px-4 py-1.5 text-xs font-medium capitalize transition-colors ${mode === m ? "bg-raiz-800 text-paper" : "text-ink/60 hover:text-ink"}`}
            >
              {m === "mes" ? "Mês" : m}
            </button>
          ))}
        </div>
      </div>

      {!agenda ? (
        <div className="mt-8 h-64 animate-pulse rounded-3xl border border-raiz-900/10 bg-cream" data-testid="agenda-loading" />
      ) : mode === "dia" ? (
        <div className="mt-8 space-y-3" data-testid="agenda-dia">
          {agenda.days[0]?.day_off && (
            <p className="rounded-2xl border border-raiz-900/10 bg-cream p-5 text-sm text-ink/60" data-testid="agenda-dia-off">
              Dia sem horários configurados. Ajuste em <strong>Disponibilidade</strong>.
            </p>
          )}
          {agenda.days[0]?.slots.map((s) => (
            <div key={s.time} data-testid={`agenda-slot-${s.time.replace(":", "")}`} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-raiz-900/10 bg-paper p-4">
              <div className="flex items-center gap-4">
                <span className="w-14 font-serif text-lg text-raiz-900">{s.time}</span>
                <StatusBadge status={s.status} />
                {s.booking && (
                  <span className="text-sm text-ink/70">
                    {s.booking.service_title} — {s.booking.name} ({MODALITY_LABEL[s.booking.modality]})
                  </span>
                )}
                {s.status === "reservado" && (
                  <span className="text-sm text-ink/60">sendo escolhido por um paciente agora</span>
                )}
                {s.expired_attempt && (
                  <span className="text-xs italic text-ink/45">houve uma tentativa de pagamento expirada ({s.expired_attempt})</span>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {canConfirmSlot(s) && (
                  <button type="button" data-testid={`slot-confirmar-${s.time.replace(":", "")}`} disabled={acting} onClick={() => bookingAction(s.booking.protocol, "confirmar")} className="rounded-full bg-raiz-800 px-4 py-2 text-xs font-medium text-paper hover:bg-raiz-700 disabled:opacity-50">
                    Confirmar
                  </button>
                )}
                {canRealizeSlot(s) && (
                  <button type="button" data-testid={`slot-realizar-${s.time.replace(":", "")}`} disabled={acting} onClick={() => bookingAction(s.booking.protocol, "realizar")} className="rounded-full bg-raiz-800 px-4 py-2 text-xs font-medium text-paper hover:bg-raiz-700 disabled:opacity-50">
                    Realizada
                  </button>
                )}
                {canCancelSlot(s) && (
                  <button type="button" data-testid={`slot-cancelar-${s.time.replace(":", "")}`} disabled={acting} onClick={() => bookingAction(s.booking.protocol, "cancelar")} className="rounded-full border border-red-500/40 px-4 py-2 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50">
                    Cancelar
                  </button>
                )}
                {(s.status === "disponivel" || s.status === "bloqueado") && (
                  <button
                    type="button"
                    data-testid={`slot-bloquear-${s.time.replace(":", "")}`}
                    disabled={acting}
                    onClick={() => toggleBlock(s, agenda.days[0].date)}
                    className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-medium transition-colors disabled:opacity-50 ${
                      s.status === "bloqueado" ? "border-raiz-800/30 text-raiz-800 hover:bg-raiz-50" : "border-red-500/40 text-red-700 hover:bg-red-50"
                    }`}
                  >
                    {s.status === "bloqueado" ? (<><Undo2 className="h-3.5 w-3.5" /> Desbloquear</>) : (<><Ban className="h-3.5 w-3.5" /> Bloquear horário</>)}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : mode === "semana" ? (
        <div className="mt-8 grid gap-3 md:grid-cols-5 xl:grid-cols-7" data-testid="agenda-semana">
          {agenda.days.map((d) => {
            const [y, m, dd] = d.date.split("-").map(Number);
            return (
              <div key={d.date} data-testid={`agenda-semana-${d.date}`} className="rounded-2xl border border-raiz-900/10 bg-paper p-3">
                <button type="button" onClick={() => openDay(d.date)} className="w-full text-left">
                  <p className="text-xs uppercase tracking-widest text-ink/45">{WEEKDAY_SHORT[d.weekday]}</p>
                  <p className="font-serif text-xl text-raiz-900">{dd}/{m}</p>
                </button>
                <div className="mt-3 space-y-1.5">
                  {d.day_off ? (
                    <p className="text-xs text-ink/35">Indisponível</p>
                  ) : (
                    d.slots.map((s) => (
                      <button
                        key={s.time}
                        type="button"
                        onClick={() => openDay(d.date)}
                        className="flex w-full items-center gap-2 rounded-lg px-2 py-1 text-left text-xs hover:bg-cream"
                        data-testid={`semana-slot-${d.date}-${s.time.replace(":", "")}`}
                      >
                        <span className="w-9 font-medium text-ink/70">{s.time}</span>
                        <StatusBadge status={s.status} />
                      </button>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <MonthGrid agenda={agenda} dayMap={dayMap} refDate={refDate} onOpenDay={openDay} />
      )}
    </div>
  );
};

const MonthGrid = ({ agenda, dayMap, refDate, onOpenDay }) => {
  const cells = useMemo(() => {
    const year = refDate.getFullYear();
    const month = refDate.getMonth();
    const firstOffset = (new Date(year, month, 1).getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const list = Array.from({ length: firstOffset }, () => null);
    for (let day = 1; day <= daysInMonth; day += 1) list.push(toISO(new Date(year, month, day)));
    return list;
  }, [refDate]);

  const summary = (d) => {
    const counts = { disponivel: 0, ocupados: 0, bloqueado: 0 };
    d.slots.forEach((s) => {
      if (s.status === "disponivel") counts.disponivel += 1;
      else if (s.status === "bloqueado") counts.bloqueado += 1;
      else if (s.status !== "cancelado") counts.ocupados += 1;
    });
    return counts;
  };

  return (
    <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7" data-testid="agenda-mes">
      {cells.map((isoDate, i) =>
        isoDate === null ? (
          <span key={`empty-${i}`} className="hidden lg:block" />
        ) : (
          <button
            key={isoDate}
            type="button"
            data-testid={`mes-dia-${isoDate}`}
            onClick={() => onOpenDay(isoDate)}
            className="rounded-2xl border border-raiz-900/10 bg-paper p-3 text-left transition-colors hover:border-gold-400/60"
          >
            <p className="font-serif text-lg text-raiz-900">{Number(isoDate.slice(-2))}</p>
            {dayMap[isoDate] && !dayMap[isoDate].day_off ? (
              <div className="mt-2 space-y-1 text-[11px] text-ink/60">
                {summary(dayMap[isoDate]).ocupados > 0 && <p>{summary(dayMap[isoDate]).ocupados} agendado(s)</p>}
                <p>{summary(dayMap[isoDate]).disponivel} livre(s)</p>
                {summary(dayMap[isoDate]).bloqueado > 0 && <p className="text-red-700">{summary(dayMap[isoDate]).bloqueado} bloqueado(s)</p>}
              </div>
            ) : (
              <p className="mt-2 text-[11px] text-ink/35">Indisponível</p>
            )}
          </button>
        )
      )}
    </div>
  );
};

export default AgendaView;
